import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  alpha,
  Skeleton
} from '@mui/material';
import {
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Mic as MicIcon,
  Groups as GroupsIcon,
  School as SchoolIcon,
  Newspaper as NewspaperIcon,
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  Public as PublicIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckCircleIcon,
  Celebration as CelebrationIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';

function LancementMoisStatistique2025() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const primaryColor = '#E30613';
  const secondaryColor = '#008D36';
  const tertiaryColor = '#f5f5f5';
  const white = '#ffffff';
  const accentColor = '#1976d2';

  const [imagesLoaded, setImagesLoaded] = useState({
    dgai: false,
    public: false
  });

  // Données principales
  const eventData = {
    date: "20 Octobre 2025",
    title: "Lancement du 14ème Mois de la Statistique & Célébration de la 4ème Journée Mondiale de la Statistique",
    themeMSB: "INNOVATIONS ET STATISTIQUES DE QUALITÉ : LEVIERS INCONTOURNABLES POUR L'ATTEINTE DE LA VISION DU BURUNDI, PAYS ÉMERGENT EN 2040 ET PAYS DÉVELOPPÉ EN 2060",
    themeJMS: "« Favoriser le changement grâce à des statistiques et des données de qualité pour tous »",
    lieu: "Siège de l'INSBU, Bujumbura",
    patron: "Sous le Haut Patronage du Cabinet de la Primature",
    animateur: "Point de presse animé par M. NDAYISENGA Modeste, Directeur Général ai de l'INSBU"
  };

  const highlights = [
    {
      icon: <EventIcon />,
      title: "Cérémonie Officielle",
      description: "Lancement solennel du 14ème Mois de la Statistique"
    },
    {
      icon: <CelebrationIcon />,
      title: "Journée Mondiale",
      description: "Célébration de la 4ème Journée Mondiale de la Statistique"
    },
    {
      icon: <MicIcon />,
      title: "Point de Presse",
      description: "Animation par le DG ai de l'INSBU"
    },
    {
      icon: <GroupsIcon />,
      title: "Mobilisation",
      description: "Participation massive du public et des médias"
    }
  ];

  const keyMessages = [
    "Lancement officiel du 14ème Mois de la Statistique au Burundi",
    "Célébration simultanée de la 4ème Journée Mondiale de la Statistique",
    "Présentation du thème national et des activités prévues",
    "Mobilisation des acteurs du Système Statistique National",
    "Lancement des premières activités de formation et de sensibilisation"
  ];

  const activitiesLaunch = [
    {
      date: "20 octobre 2025",
      title: "Cérémonie de lancement officielle",
      details: "Discours officiels et présentation du programme du 14ème Mois de la Statistique"
    },
    {
      date: "20-29 octobre 2025",
      title: "Formation du personnel de terrain - Enquête Nationale Agricole Rénovée du Burundi (ENArB)",
      details: "Début des premières sessions de formation sur la collecte des données agricoles"
    }
  ];

  // Photos du lancement
  const lancementPhotos = [
    {
      id: 1,
      src: "/assets/msb/dgai_anime_presse_lancement_msb2025_celebration_jms2025.jpg",
      alt: "M. NDAYISENGA Modeste, DG ai de l'INSBU, animant le point de presse",
      title: "Point de Presse",
      description: "Animation par le Directeur Général ai de l'INSBU"
    },
    {
      id: 2,
      src: "/assets/msb/public_jms_lancement_msb.jpg",
      alt: "Public participant à la cérémonie de lancement du MSB 2025",
      title: "Public et Participants",
      description: "Audience présente lors du lancement officiel"
    }
  ];

  // Composant Image Optimisé
  const OptimizedImage = ({ src, alt, height = 300, objectFit = 'cover', priority = false, borderRadius = 2 }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
      if (priority) {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          setIsLoading(false);
        };
        img.onerror = () => {
          setIsLoading(false);
          setHasError(true);
        };
      }
    }, [src, priority]);

    const handleImageLoad = () => {
      setIsLoading(false);
    };

    const handleImageError = () => {
      setIsLoading(false);
      setHasError(true);
    };

    if (hasError) {
      return (
        <Box sx={{ 
          position: 'relative', 
          height: height, 
          width: '100%', 
          overflow: 'hidden', 
          borderRadius: borderRadius,
          bgcolor: '#f0f0f0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <PhotoCameraIcon sx={{ fontSize: 40, color: '#999', mb: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Image non disponible
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        position: 'relative', 
        height: height, 
        width: '100%', 
        overflow: 'hidden', 
        borderRadius: borderRadius,
        bgcolor: '#f5f5f5'
      }}>
        {isLoading && (
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={height} 
            animation="wave"
            sx={{ position: 'absolute', top: 0, left: 0 }}
          />
        )}
        <Box
          component="img"
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: objectFit,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.3s ease, transform 0.5s ease',
            '&:hover': {
              transform: 'scale(1.03)'
            }
          }}
          loading={priority ? "eager" : "lazy"}
        />
      </Box>
    );
  };

  // Fonction de navigation
  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: tertiaryColor,
      background: `linear-gradient(135deg, ${alpha(tertiaryColor, 1)} 0%, ${alpha(primaryColor, 0.03)} 100%)`
    }}>
      <NavigationBar />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Bouton retour */}
        <Fade in={true} timeout={500}>
          <Button
            onClick={() => handleNavigation('/actualitesEtEvenements')}
            startIcon={<ArrowBackIcon />}
            sx={{ 
              mb: 4, 
              color: primaryColor,
              fontWeight: 'bold'
            }}
          >
            Retour aux actualités
          </Button>
        </Fade>

        {/* En-tête Principal */}
        <Fade in={true} timeout={800}>
          <Paper 
            elevation={0}
            sx={{ 
              p: isMobile ? 3 : 4, 
              mb: 4, 
              textAlign: 'center', 
              bgcolor: white,
              borderTop: `6px solid ${secondaryColor}`,
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Chip 
              icon={<CalendarIcon />}
              label={eventData.date}
              sx={{ 
                bgcolor: primaryColor, 
                color: 'white', 
                fontWeight: 'bold',
                mb: 3,
                fontSize: '0.9rem',
                height: 36
              }} 
            />
            
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                color: secondaryColor,
                mb: 3,
                lineHeight: 1.3
              }}
            >
              {eventData.title}
            </Typography>
            
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 3 }}>
              <Chip 
                icon={<EventIcon />}
                label="Lancement MSB 2025"
                sx={{ borderColor: primaryColor, color: primaryColor }}
                variant="outlined"
              />
              <Chip 
                icon={<CelebrationIcon />}
                label="Journée Mondiale Statistique"
                sx={{ borderColor: secondaryColor, color: secondaryColor }}
                variant="outlined"
              />
            </Stack>
            
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: secondaryColor,
                fontWeight: 'medium',
                mb: 2
              }}
            >
              {eventData.lieu} • {eventData.patron}
            </Typography>
          </Paper>
        </Fade>

        {/* Section Thèmes */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {/* Thème MSB */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1000}>
              <Paper 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  bgcolor: alpha(primaryColor, 0.05),
                  border: `2px solid ${alpha(primaryColor, 0.2)}`,
                  borderRadius: 3
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ bgcolor: primaryColor, mr: 2 }}>
                    <TrendingIcon />
                  </Avatar>
                  <Typography variant="h5" sx={{ color: primaryColor, fontWeight: 'bold' }}>
                    Thème du Mois de la Statistique
                  </Typography>
                </Box>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontStyle: 'italic',
                    color: secondaryColor,
                    lineHeight: 1.5,
                    textAlign: 'center',
                    fontWeight: 'medium'
                  }}
                >
                  {eventData.themeMSB}
                </Typography>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Objectifs stratégiques :
                  </Typography>
                  <Stack spacing={1}>
                    {['Promouvoir l\'innovation statistique', 'Garantir la qualité des données', 'Soutenir la vision 2040-2060', 'Renforcer le Système Statistique National'].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ color: secondaryColor, fontSize: 16, mr: 1 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Paper>
            </Grow>
          </Grid>

          {/* Thème JMS */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1200}>
              <Paper 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  bgcolor: alpha(secondaryColor, 0.05),
                  border: `2px solid ${alpha(secondaryColor, 0.2)}`,
                  borderRadius: 3
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ bgcolor: secondaryColor, mr: 2 }}>
                    <PublicIcon />
                  </Avatar>
                  <Typography variant="h5" sx={{ color: secondaryColor, fontWeight: 'bold' }}>
                    Thème de la Journée Mondiale
                  </Typography>
                </Box>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontStyle: 'italic',
                    color: primaryColor,
                    lineHeight: 1.5,
                    textAlign: 'center',
                    fontWeight: 'medium'
                  }}
                >
                  {eventData.themeJMS}
                </Typography>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Engagements internationaux :
                  </Typography>
                  <Stack spacing={1}>
                    {['Données de qualité accessibles à tous', 'Innovation pour le changement', 'Inclusion et équité', 'Développement durable'].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ color: primaryColor, fontSize: 16, mr: 1 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Paper>
            </Grow>
          </Grid>
        </Grid>

        {/* Section Photos */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: primaryColor, 
              fontWeight: 'bold', 
              mb: 4,
              textAlign: 'center'
            }}
          >
            <PhotoCameraIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Moments Forts du Lancement
          </Typography>

          <Grid container spacing={3}>
            {lancementPhotos.map((photo, index) => (
              <Grid item xs={12} md={6} key={photo.id}>
                <Grow in={true} timeout={800 + (index * 200)}>
                  <Card sx={{ 
                    height: '100%',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)'
                    }
                  }}>
                    <OptimizedImage 
                      src={photo.src}
                      alt={photo.alt}
                      height={isMobile ? 250 : 350}
                      objectFit="contain"
                      priority={index === 0}
                      borderRadius={2}
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: index % 2 === 0 ? primaryColor : secondaryColor, fontWeight: 'bold' }}>
                        {photo.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {photo.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Section Points Forts */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: secondaryColor, 
              fontWeight: 'bold', 
              mb: 4,
              textAlign: 'center'
            }}
          >
            Points Forts de l'Événement
          </Typography>

          <Grid container spacing={3}>
            {highlights.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in={true} timeout={500 + (index * 200)}>
                  <Paper 
                    sx={{ 
                      p: 3,
                      textAlign: 'center',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[4],
                        bgcolor: alpha(primaryColor, 0.03)
                      }
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        bgcolor: index % 2 === 0 ? primaryColor : secondaryColor,
                        color: 'white',
                        width: 56,
                        height: 56,
                        mb: 2,
                        mx: 'auto'
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Section Messages Clés */}
        <Paper 
          sx={{ 
            p: 4, 
            mb: 6,
            bgcolor: white,
            borderRadius: 3,
            borderLeft: `4px solid ${secondaryColor}`
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              color: primaryColor,
              fontWeight: 'bold',
              mb: 3
            }}
          >
            <MicIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Messages Clés du Lancement
          </Typography>

          <Grid container spacing={2}>
            {keyMessages.map((message, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, p: 1 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(primaryColor, 0.1), 
                      color: primaryColor,
                      width: 28,
                      height: 28,
                      mr: 2,
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                    {message}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Section Activités Lancement */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: primaryColor, 
              fontWeight: 'bold', 
              mb: 4,
              textAlign: 'center'
            }}
          >
            Activités de Lancement
          </Typography>

          <Grid container spacing={3}>
            {activitiesLaunch.map((activity, index) => (
              <Grid item xs={12} key={index}>
                <Grow in={true} timeout={300 + (index * 200)}>
                  <Paper 
                    sx={{ 
                      p: 3,
                      bgcolor: white,
                      borderRadius: 2,
                      borderLeft: `4px solid ${index % 2 === 0 ? primaryColor : secondaryColor}`,
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Chip 
                        label={activity.date}
                        size="small"
                        sx={{ 
                          bgcolor: index % 2 === 0 ? primaryColor : secondaryColor,
                          color: 'white',
                          mr: 2,
                          fontWeight: 'bold'
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {activity.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {activity.details}
                    </Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Section Liens et Navigation */}
        <Paper 
          sx={{ 
            p: 4, 
            mb: 4,
            bgcolor: alpha(secondaryColor, 0.05),
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ color: secondaryColor, fontWeight: 'bold', mb: 3 }}>
            Explorez les Autres Événements
          </Typography>
          
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={4}>
              <Button
                onClick={() => handleNavigation('/moisStatistique2025')}
                variant="contained"
                fullWidth
                startIcon={<NewspaperIcon />}
                sx={{ 
                  bgcolor: primaryColor,
                  py: 1.5,
                  '&:hover': { bgcolor: '#c60511' }
                }}
              >
                Programme Complet
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Button
                onClick={() => handleNavigation('/clotureMoisStatistique2025')}
                variant="outlined"
                fullWidth
                startIcon={<EventIcon />}
                sx={{ 
                  borderColor: secondaryColor,
                  color: secondaryColor,
                  py: 1.5
                }}
              >
                Cérémonie de Clôture
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Button
                onClick={() => handleNavigation('/actualitesEtEvenements')}
                variant="outlined"
                fullWidth
                startIcon={<ArrowBackIcon />}
                sx={{ 
                  borderColor: primaryColor,
                  color: primaryColor,
                  py: 1.5
                }}
              >
                Toutes les Actualités
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Section Conclusion */}
        <Fade in={true} timeout={1500}>
          <Box sx={{ textAlign: 'center', mt: 6, mb: 4 }}>
            <Paper 
              sx={{ 
                p: 4,
                display: 'inline-block',
                borderRadius: 2,
                bgcolor: white,
                border: `2px solid ${alpha(secondaryColor, 0.3)}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
              }}
            >
              <Typography 
                variant="h5" 
                component="h3"
                sx={{ 
                  fontStyle: 'italic',
                  color: secondaryColor,
                  fontWeight: 'bold',
                  lineHeight: 1.5
                }}
              >
                Le lancement du 14ème Mois de la Statistique marque le début d'une série d'activités<br />
                visant à promouvoir l'innovation et la qualité statistique pour soutenir<br />
                la vision Burundi 2040-2060.
              </Typography>
            </Paper>
            
            <Box sx={{ mt: 3 }}>
              <Button 
                onClick={() => handleNavigation('/actualitesEtEvenements')}
                variant="outlined" 
                startIcon={<ArrowBackIcon />}
                sx={{ 
                  borderColor: accentColor, 
                  color: accentColor,
                  '&:hover': { borderColor: accentColor, bgcolor: alpha(accentColor, 0.1) },
                  px: 3, 
                  py: 1.2, 
                  fontWeight: 'bold' 
                }}
              >
                Toutes les actualités du MSB
              </Button>
            </Box>
          </Box>
        </Fade>

        {/* Section Signature */}
        <Paper 
          sx={{ 
            p: 4, 
            mb: 8, 
            bgcolor: white, 
            borderRadius: 3,
            border: `1px solid ${alpha(accentColor, 0.2)}`, 
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)' 
          }}
        >
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              color: accentColor, 
              fontWeight: 'bold', 
              mb: 3 
            }}
          >
            Présenté par Ir. Léandre NIMPAGARITSE, Informaticien – Agent de publication, diffusion et communication à l'INSBU.
          </Typography>
        </Paper>
      </Container>
      
      <Footer />
    </Box>
  );
}

export default LancementMoisStatistique2025;